import { useEffect, useRef, useState } from 'react';

/** Strong ease-out (Emil's curve) sampled for the count-up tween. */
function easeOut(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export interface AnimatedNumberProps {
  /** Target numeric value. */
  value: number;
  /** Formats the (interpolated) number for display — e.g. currency / integer. */
  format: (n: number) => string;
  /** Tween length (ms). Kept short so the dashboard feels snappy, not laggy. */
  durationMs?: number;
}

/**
 * Counts a number up to `value` on mount / when it changes — a perceived-
 * performance touch for KPI surfaces. Animates via requestAnimationFrame using
 * a strong ease-out; respects prefers-reduced-motion (jumps straight to the
 * final value). Renders the formatted string only, so it drops into any layout.
 */
export function AnimatedNumber({ value, format, durationMs = 650 }: AnimatedNumberProps): JSX.Element {
  // Start at 0 so the value counts up on first mount (KPIs mount once their
  // data arrives); subsequent changes tween from the previous value.
  const [display, setDisplay] = useState(0);
  const fromRef = useRef(0);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    if (prefersReducedMotion() || fromRef.current === value) {
      fromRef.current = value;
      setDisplay(value);
      return undefined;
    }
    const from = fromRef.current;
    const delta = value - from;
    let start: number | null = null;
    const step = (ts: number): void => {
      if (start === null) start = ts;
      const p = Math.min(1, (ts - start) / durationMs);
      setDisplay(from + delta * easeOut(p));
      if (p < 1) rafRef.current = requestAnimationFrame(step);
      else fromRef.current = value;
    };
    rafRef.current = requestAnimationFrame(step);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [value, durationMs]);

  return <>{format(display)}</>;
}
